import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listSales } from '../services/salesService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import SearchInput from '../components/ui/SearchInput';
import Pagination from '../components/ui/Pagination';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function SalesPage() {
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!ruc) return;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await listSales({
          ruc,
          search,
          includeCancelled,
          cursor: reset ? null : cursor,
        });
        setItems((prev) => (reset ? result.items : [...prev, ...result.items]));
        setCursor(result.cursor || null);
        setHasMore(!!result.hasMore);
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruc, search, includeCancelled, cursor]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setItems([]);
      setCursor(null);
      fetchPage({ reset: true });
    }, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [ruc, search, includeCancelled]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    { key: 'pedido', label: 'Pedido', render: (r) => `#${r.pedido ?? '—'}` },
    {
      key: 'cliente',
      label: 'Cliente',
      render: (r) => r.clienteNombre || r.cliente || '—',
    },
    { key: 'fecha', label: 'Fecha', render: (r) => formatDate(r.fecha) },
    { key: 'total', label: 'Total', render: (r) => formatMoney(r.total) },
    {
      key: 'status',
      label: 'Estado',
      render: (r) => (
        <span>
          <Badge tone={r.status === 'pagado' ? 'ok' : 'neutral'}>{r.status || '—'}</Badge>
          {r.cancelado ? <Badge tone="muted"> cancelado</Badge> : null}
        </span>
      ),
    },
    {
      key: 'comprobante',
      label: 'Comprobante',
      render: (r) =>
        r.comprobanteEmitido ? (
          <Badge tone="info">
            {r.tipoComprobante || 'comp.'} {r.comprobanteSerie || ''}-{r.comprobanteCorrelativo || ''}
          </Badge>
        ) : (
          <span className="muted">Sin emitir</span>
        ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (r) => (
        <button
          type="button"
          className="btn btn-mini"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/ventas/${r.id}`);
          }}
        >
          Ver
        </button>
      ),
    },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Ventas</h1>
          <p className="muted">
            Colección <code>pedidos</code> del tenant · lazy limit 10 · no es una segunda facturación
          </p>
        </div>
      </header>

      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar pedido o cliente…" />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeCancelled}
            onChange={(e) => setIncludeCancelled(e.target.checked)}
          />
          Incluir cancelados
        </label>
      </div>

      {loading ? (
        <LoadingState label="Cargando ventas…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchPage({ reset: true })} />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={items}
            keyField="id"
            onRowClick={(row) => navigate(`/admin/ventas/${row.id}`)}
            empty={<EmptyState title="No hay ventas" description="Los pedidos aparecen aquí al vender desde la app." />}
          />
          <Pagination hasMore={hasMore} onMore={() => fetchPage()} loading={loadingMore} shown={items.length} />
        </>
      )}
    </div>
  );
}
